// Opened FROM EDEN (Eden 0.42.54, editor-launch.ts): Eden puts a one-day key for the
// TraceCAD service identity in the URL fragment, `#eden=<base64url JSON>`. A fragment
// never reaches a server or a log. It is read here, stored, and removed from the
// address bar before anything else runs, so a copied link or a reload never carries it.
//
// Small and eager on purpose: it runs before the lazy Eden chunk, and the config
// resolver asks it first. No Eden network code lives here.
import type { EdenConfig } from "./client";

const LS = "tracecad.eden.launch";

/** Where a new drawing belongs, as Eden sent it. */
export interface LaunchHost {
  form_tid: string;
  record_id: string;
  label: string;
  /** The Project File field the host fills, e.g. `project_ref` or `tender_ref`. */
  field: string;
}

export interface LaunchInfo {
  mode: "open" | "new";
  host?: LaunchHost;
  /** Every reference the new file should carry, by Project File field name. */
  refs?: Record<string, string>;
}

interface Stored {
  apiKey: string;
  accountId: string;
  sourceApp: string;
  exp: number;
  forms?: EdenConfig["forms"];
}

function decode(b64url: string): unknown {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

/** `edenForms=<job>,<projectFile>,<register>` — TraceCAD's own registration adds it. */
export function formsFromQuery(search: string): EdenConfig["forms"] | undefined {
  const raw = new URLSearchParams(search).get("edenForms");
  if (!raw) return undefined;
  const [job, projectFile, register] = raw.split(",").map((s) => s.trim());
  if (!job || !projectFile) return undefined;
  return { job, projectFile, ...(register ? { register } : {}) };
}

/** Parse a fragment payload. Pure — the tests hold it. Null when it is not a valid launch. */
export function parseLaunch(hash: string, now: number): (Stored & LaunchInfo) | null {
  const m = /(?:^#|&)eden=([A-Za-z0-9_-]+)/.exec(hash);
  if (!m) return null;
  let p: Record<string, unknown>;
  try {
    p = decode(m[1]!) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (p.v !== 1 || typeof p.apiKey !== "string" || typeof p.accountId !== "string" || typeof p.sourceApp !== "string") return null;
  if (typeof p.exp !== "number" || p.exp <= now) return null;
  const mode = p.mode === "new" ? "new" : "open";
  const h = p.host as Record<string, unknown> | undefined;
  const host =
    h && typeof h.record_id === "string" && typeof h.form_tid === "string"
      ? { form_tid: h.form_tid, record_id: h.record_id, label: typeof h.label === "string" ? h.label : "Eden record", field: typeof h.field === "string" ? h.field : "project_ref" }
      : undefined;
  const refs: Record<string, string> = {};
  if (p.refs && typeof p.refs === "object") {
    for (const [k, v] of Object.entries(p.refs as Record<string, unknown>)) if (typeof v === "string") refs[k] = v;
  }
  if (mode === "new" && !host) return null;
  return { apiKey: p.apiKey, accountId: p.accountId, sourceApp: p.sourceApp, exp: p.exp, mode, host, refs };
}

/**
 * Read a launch from the address, store its key, and clean the address. Call once,
 * first thing at boot. Returns what Eden asked for, or null for an ordinary start.
 */
export function takeLaunch(): LaunchInfo | null {
  if (typeof location === "undefined" || !location.hash.includes("eden=")) return null;
  const parsed = parseLaunch(location.hash, Date.now());
  // The key leaves the address bar whether or not it was usable.
  history.replaceState(null, "", location.pathname + location.search);
  if (!parsed) return null;
  let forms = formsFromQuery(location.search);
  if (!forms) {
    try {
      const prev = JSON.parse(localStorage.getItem(LS) ?? "null") as Stored | null;
      if (prev?.accountId === parsed.accountId) forms = prev.forms;
    } catch {
      /* none kept */
    }
  }
  const stored: Stored = { apiKey: parsed.apiKey, accountId: parsed.accountId, sourceApp: parsed.sourceApp, exp: parsed.exp, ...(forms ? { forms } : {}) };
  try {
    localStorage.setItem(LS, JSON.stringify(stored));
  } catch {
    /* storage refused: this tab still has nothing to sign in with */
  }
  return { mode: parsed.mode, host: parsed.host, refs: parsed.refs };
}

/** The launch key as a config, while it is valid. Same-origin proxy, never the Eden host. */
export function launchConfig(): EdenConfig | null {
  try {
    const s = JSON.parse(localStorage.getItem(LS) ?? "null") as Stored | null;
    if (!s) return null;
    if (s.exp <= Date.now()) {
      localStorage.removeItem(LS);
      return null;
    }
    const base = `${location.origin}${import.meta.env.BASE_URL}`.replace(/\/$/, "");
    return { url: `${base}/eden-api`, apiKey: s.apiKey, accountId: s.accountId, sourceApp: s.sourceApp, ...(s.forms ? { forms: s.forms } : {}) };
  } catch {
    return null;
  }
}

/** Opened from Eden with a key that still works: saving goes to Eden by itself. */
export function isLaunched(): boolean {
  try {
    const s = JSON.parse(localStorage.getItem(LS) ?? "null") as Stored | null;
    return !!s && s.exp > Date.now();
  } catch {
    return false;
  }
}
